import * as React from 'react';
import { memo } from 'react';
import { useTranslation } from 'next-i18next';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { cn, toDateFormat } from '@restworld/utils-common';
import { CardPrimary } from '../../design-system';
import { Text } from '@restworld/ui-ds-base';
import { JobPositionPreviewBlockProps } from './types';
import { useJobPositionDetailsPreview } from './hooks';

/**
 * JobInfoCardPreview - Card con le informazioni principali della job position
 * Versione semplificata senza pulsanti di candidatura e interazioni
 */
export const JobInfoCardPreview = memo(({ jobPosition }: JobPositionPreviewBlockProps) => {
  const { t } = useTranslation('common');
  const { jobPositionDetails, showRestaurant, publishedAt } = useJobPositionDetailsPreview(jobPosition);

  return (
    <JobInfoWrapper showRestaurant={showRestaurant || false}>
      <Wrapper>
        <CardPrimary
          jobPositionDetails={jobPositionDetails}
          showRestaurant={showRestaurant || false}
          showViolaBadge={false}
        />
      </Wrapper>
      {publishedAt && (
        <FooterWrapper>
          <JobDetailParagraph icon={<CalendarIcon className="w-[13.5px] h-[13.5px] text-white" />}>
            {t('job_position_preview_published_on', { date: toDateFormat(publishedAt, `dd/MM/yyyy`) })}
          </JobDetailParagraph>
        </FooterWrapper>
      )}
    </JobInfoWrapper>
  );
});

JobInfoCardPreview.displayName = 'JobInfoCardPreview';

const JobInfoWrapper = ({ showRestaurant, className, ...props }: any) => (
  <div
    className={cn(
      'rounded-[20px] w-full h-max bg-primary-800',
      'lg:max-w-sm lg:min-w-[384px]',
      'px-6 pb-6 lg:shadow',
      'lg:sticky lg:top-2',
      showRestaurant ? 'pt-16' : 'pt-6',
      className
    )}
    {...props}
  />
);

const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-6 items-center border-b border-primary-300 pb-4', className)} {...props} />
);
const FooterWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4 items-center justify-start w-full pt-4', className)} {...props} />
);

const JobDetailParagraph = ({ children, icon }: { children: React.ReactNode; icon: React.ReactNode }) => (
  <div className="flex flex-row gap-2 items-center justify-start w-fit">
    {icon}
    <Text size={1} color="white">
      {children}
    </Text>
  </div>
);
